import { classNames } from '@shopify/css-utilities'
import { Image } from '@shopify/hydrogen'
import { Image as ImageType, Maybe } from '@shopify/hydrogen/storefront-api-types'
import { AnimatePresence, motion, useScroll } from 'framer-motion'
import { useContext, useEffect, useRef } from 'react'
import styles from '../../assets/scss/components/desktop-menu.module.scss'
import { EnhancedMenu } from '../../lib/menus'
import { HeaderContext } from '../header/header-provider.client'
import { BreakpointsContext } from '../layout/breakpoints-provider.client'
import { AdditionalMenu } from './additional-menu.client'
import { ChildMenu } from './child-menu.client'
import { ParentMenu } from './parent-menu.client'
import { LanguageSwitch } from '../language/language-switch.client'

const menuItemHeight = 27

export const DesktopMenu: React.FC<{
  allStarsMenu: EnhancedMenu
  mainMenu: EnhancedMenu
  takeATripMenu: EnhancedMenu
}> = ({ allStarsMenu, mainMenu, takeATripMenu }) => {
  const { activeChildMenuItem, hoveredParentMenuItem, isMainNavigationOpen, setIsMainNavigationOpen } =
    useContext(HeaderContext)
  const { isLargeUp } = useContext(BreakpointsContext)

  if (!isLargeUp) {
    return null
  }

  return (
    <>
      <AnimatePresence>
        {isMainNavigationOpen && (
          <motion.div
            animate={{ opacity: 1 }}
            className={styles.overlay}
            exit={{ opacity: 0, transition: { ease: [1, 0, 1, 1] } }}
            initial={{ opacity: 0 }}
            onClick={() => setIsMainNavigationOpen(false)}
            transition={{ ease: [0, 0, 0, 1] }}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {isMainNavigationOpen && (
          <MenuDrawer allStarsMenu={allStarsMenu} mainMenu={mainMenu} takeATripMenu={takeATripMenu} />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {isMainNavigationOpen && (activeChildMenuItem?.image || hoveredParentMenuItem?.image) && (
          <motion.div
            animate={{ translateX: 0 }}
            className={styles.imageDrawer}
            exit={{ translateX: '-100%', transition: { ease: [1, 0, 1, 1] } }}
            initial={{ translateX: '-100%' }}
            transition={{ ease: [0, 0, 0, 1] }}>
            <ChildMenuItemImage image={activeChildMenuItem?.image || hoveredParentMenuItem?.image} />
            <div className={styles.imageDescription}>
              {activeChildMenuItem?.imageDescription || hoveredParentMenuItem?.imageDescription}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

const MenuDrawer: React.FC<{
  allStarsMenu: EnhancedMenu
  mainMenu: EnhancedMenu
  takeATripMenu: EnhancedMenu
}> = ({ allStarsMenu, mainMenu, takeATripMenu }) => {
  const {
    activeAdditionalMenuItem,
    activeChildMenuItem,
    activeDesktopParentMenuIndex,
    burgerIconOpacity,
    hoveredParentMenuItem,
  } = useContext(HeaderContext)
  const maxMenuItems = Math.max(...mainMenu.items.map((item) => item.items.length))
  const ref = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({
    container: ref,
  })

  useEffect(() => {
    const unsubscribeScrollYProgress = scrollYProgress.onChange((y) => {
      if (ref.current) {
        burgerIconOpacity.set(ref.current.scrollHeight > ref.current.clientHeight ? Math.max(1 - 4 * y, 0) : 1)
      }
    })

    return () => unsubscribeScrollYProgress()
  }, [ref])

  return (
    <motion.div
      animate={{ translateX: 0 }}
      className={classNames(
        styles.menuDrawer,
        (activeAdditionalMenuItem || activeChildMenuItem) && styles.hasActiveHover,
      )}
      exit={{ translateX: '-100%', transition: { ease: [1, 0, 1, 1] } }}
      initial={{ translateX: '-100%' }}
      ref={ref}
      transition={{ ease: [0, 0, 0, 1] }}>
      <div className={styles.menuContainer}>
        <div className={styles.parentChildMenu} style={{ height: maxMenuItems * menuItemHeight }}>
          <ParentMenu menu={mainMenu} />
          <motion.div className={classNames(styles.menuRightSide, hoveredParentMenuItem?.image && styles.hide)}>
            <ChildMenu menu={mainMenu} />
          </motion.div>
        </div>
        <motion.div
          className={classNames(
            styles.menuRightSide,
            (activeDesktopParentMenuIndex !== 0 || hoveredParentMenuItem?.image) && styles.hide,
          )}>
          <AdditionalMenu menu={takeATripMenu} />
          <AdditionalMenu menu={allStarsMenu} />
        </motion.div>
      </div>
      <LanguageSwitch />
    </motion.div>
  )
}

const ChildMenuItemImage: React.FC<{ image?: Maybe<ImageType> }> = ({ image }) => {
  if (image) {
    return (
      <Image alt={image.altText ?? ''} height={image.height ?? 'auto'} src={image.url} width={image.width ?? 'auto'} />
    )
  }

  return null
}
